/* eslint-disable no-console */
import { type EnvironmentConfig, getEnvironmentConfig } from './environments'

export interface AppConfig {
  environment: EnvironmentConfig
  version: string
  buildTime: string
  buildDate: Date
  isDevelopment: boolean
  isProduction: boolean
  isStaging: boolean
  isTest: boolean
}

class ConfigManager {
  private static instance: ConfigManager
  private config: AppConfig

  private constructor() {
    const envConfig = getEnvironmentConfig()

    this.config = {
      environment: envConfig,
      version: __APP_VERSION__,
      buildTime: __BUILD_TIME__,
      buildDate: new Date(__BUILD_TIME__),
      isDevelopment: process.env.NODE_ENV === 'development',
      isProduction: process.env.NODE_ENV === 'production',
      isStaging: process.env.NODE_ENV === 'staging',
      isTest: process.env.NODE_ENV === 'test',
    }
  }

  public static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager()
    }

    return ConfigManager.instance
  }

  public getConfig(): AppConfig {
    return this.config
  }

  public getEnvironment(): EnvironmentConfig {
    return this.config.environment
  }

  public getApiBaseUrl(): string {
    return this.config.environment.apiBaseUrl
  }

  public getAuthApiUrl(): string {
    return this.config.environment.authApiUrl
  }

  public getHttpTimeout(): number {
    return this.config.environment.httpTimeout
  }

  public isDebugEnabled(): boolean {
    return this.config.environment.enableDebug
  }

  public isAnalyticsEnabled(): boolean {
    return this.config.environment.enableAnalytics
  }

  public isErrorReportingEnabled(): boolean {
    return this.config.environment.enableErrorReporting
  }

  public getLogLevel(): string {
    return this.config.environment.logLevel
  }

  public getFeatures() {
    return this.config.environment.features
  }

  public getPerformance() {
    return this.config.environment.performance
  }

  public getSecurity() {
    return this.config.environment.security
  }

  public getWebSocket() {
    return this.config.environment.websocket
  }

  public getWsBaseUrl(): string {
    return this.config.environment.wsBaseUrl
  }

  public getWpcauthDevToken(): string {
    return this.config.environment.wpcauthDevToken
  }

  public isProtobufCheckpointLoggingEnabled(): boolean {
    return this.config.environment.enableProtobufCheckpointLogging
  }

  public isSpreadXmlLoggingEnabled(): boolean {
    return this.config.environment.enableSpreadXmlLogging
  }

  public getDevLandingPageNumber(): number {
    return this.config.environment.devLandingPageNumber
  }

  public getEmbedParentOrigins(): string[] {
    return this.config.environment.embedParentOrigins
  }

  public logConfig(): void {
    if (this.isDebugEnabled()) {
      console.group('🔧 Application Configuration')
      console.log('Environment:', this.config.environment.name)
      console.log('Version:', this.config.version)
      console.log('Build Time:', this.config.buildTime)
      console.log('API Base URL:', this.config.environment.apiBaseUrl)
      console.log('Debug Enabled:', this.config.environment.enableDebug)
      console.log('Analytics Enabled:', this.config.environment.enableAnalytics)
      console.log('Error Reporting Enabled:', this.config.environment.enableErrorReporting)
      console.log('Log Level:', this.config.environment.logLevel)
      console.groupEnd()
    }
  }
}

// Export singleton instance
export const configManager = ConfigManager.getInstance()

// Convenience functions
export const getConfig = (): AppConfig => configManager.getConfig()
export const getEnvironment = (): EnvironmentConfig => configManager.getEnvironment()
export const getApiBaseUrl = (): string => configManager.getApiBaseUrl()
export const getAuthApiUrl = (): string => configManager.getAuthApiUrl()
export const getHttpTimeout = (): number => configManager.getHttpTimeout()
export const isDebugEnabled = (): boolean => configManager.isDebugEnabled()
export const isDevelopmentMode = (): boolean => configManager.getConfig().isDevelopment
export const isAnalyticsEnabled = (): boolean => configManager.isAnalyticsEnabled()
export const isErrorReportingEnabled = (): boolean => configManager.isErrorReportingEnabled()
export const getLogLevel = (): string => configManager.getLogLevel()
export const getFeatures = () => configManager.getFeatures()
export const getPerformance = () => configManager.getPerformance()
export const getSecurity = () => configManager.getSecurity()
export const getWebSocket = () => configManager.getWebSocket()
export const getWsBaseUrl = (): string => configManager.getWsBaseUrl()
export const getWpcauthDevToken = (): string => configManager.getWpcauthDevToken()
export const isProtobufCheckpointLoggingEnabled = (): boolean =>
  configManager.isProtobufCheckpointLoggingEnabled()
export const isSpreadXmlLoggingEnabled = (): boolean => configManager.isSpreadXmlLoggingEnabled()
export const getDevLandingPageNumber = (): number => configManager.getDevLandingPageNumber()
export const getEmbedParentOrigins = (): string[] => configManager.getEmbedParentOrigins()
export const logConfig = (): void => configManager.logConfig()
